import * as React from 'react';
import { connect } from 'react-redux';
import { debounce, isEmpty, isEqual, isNil, isNumber } from 'lodash';

import { navigate } from 'libs/router';

import Input from 'components/inputs/input/Input';
import Tooltip from 'components/Tooltip';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Pagination from 'components/pagination/Pagination';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import { ModalFrameEntity, ModalFrameMessage } from 'components/modal';
import { getMostActiveState } from 'components/tabs/tabs-underlined/tabs-underlined-utils';
import CreatorTileSizing from 'components/creator-tile/sizing/CreatorTileSizing';

import { getTileSize, setTileSize } from 'modules/discovery/creators/utils/creators';

import ListsResults from '../ListsResults';
import ListsSort from '../lists-sort/ListsSort';
import ListsViewPanel from '../lists-view-panel/ListsViewPanel';
import ListsStats from '../stats/ListsStats';

import {
  fetchList,
  fetchListInfluencers,
  removeSingleInfluencerFromList,
  resetLists
} from '../../reducers/lists';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { trackEventListsStats } from 'modules/tracking/utils/tracking';

import { __t, applyTranslations } from 'utils/language';
import { hasProposalPage } from 'utils/realm-outfits';
import { Querystring } from 'utils/url';
import { getItem, removeItem } from 'utils/storage/sessionStorage';
import { updateQueryParams } from 'utils/location';
import {
  CREATORS,
  LIST_STATE_STORAGE_NAME,
  SMART_LIST_TYPE,
  STATS,
  getManageListStatus,
  getQueryValueFromUrl
} from 'utils/list';

import './lists-view.scss';

import type {
  InfluencerTile,
  List,
  ListConfirmModalProps,
  ListInfluencerOptions,
  ListScreenState,
  ListsStatsView,
  LocationWindow,
  Organization,
  ParamsLists,
  ProposalListMemberStatus,
  TextEntry
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./lists-view.i18n.json'));

type UrlParams = {
  [x: string]: string | number
};

type CreatorProposalStatus = {
  [x: string]: ProposalListMemberStatus
};

type OwnProps = {
  params: ParamsLists,
  location: LocationWindow
};

type StateProps = {
  currentOrganization: Organization,
  organizationsListIds: Array<number>,
  influencers: Array<InfluencerTile>,
  creatorProposalStatus: CreatorProposalStatus,
  list: List,
  sort: string,
  screenState: ListScreenState,
  isLoading: boolean
};

type DispatchProps = {
  dispatchFetchList: (orgId: number, listId: number, channel: string) => void,
  dispatchFetchListInfluencers: (
    orgId: number,
    listId: number,
    name: string,
    page: number | undefined | null,
    options: ListInfluencerOptions
  ) => void,
  dispatchResetLists: () => void,
  dispatchDeleteInfluencerFromList: (orgId: number, listId: number, influencerId: number) => Promise<void>,
  dispatchShowConfirmationModal: (props: ListConfirmModalProps) => void,
  dispatchHideConfirmationModal: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  expandedIdsMap: {
    [type: number]: string
  },
  searchByName: string,
  view: ListsStatsView,
  channel: 'all' | 'instagram' | 'tiktok'
};

const DEBOUNCE_DELAY = 500;
const msgKey = 'lists_view';

class ListsView extends React.PureComponent<Props, State> {
  state: State = {
    expandedIdsMap: {},
    searchByName: getQueryValueFromUrl(this.props.location, 'name') || '',
    channel: 'all',
    view: CREATORS
  };

  debounceMethod: ((urlParams?: UrlParams) => void) | null = null;

  constructor(props: Props) {
    super(props);

    this.debounceMethod = debounce(this.handleReplacePage, DEBOUNCE_DELAY);
  }

  componentDidMount() {
    const { location } = this.props;
    const prevPage = getQueryValueFromUrl(location, 'prev');

    if (isEmpty(prevPage)) {
      removeItem(LIST_STATE_STORAGE_NAME);
    }

    this.loadData();
  }

  componentDidUpdate(prevProps: Props) {
    const { influencers, location, screenState } = this.props;
    const { expandedIdsMap } = this.state;

    if (!isEqual(prevProps.screenState.sort, screenState.sort)) {
      const name = getQueryValueFromUrl(location, 'name');

      this.handleReplacePage({ name });
    }

    if (
      !isEqual(prevProps.location.query.page, location.query.page)
      || !isEqual(prevProps.location.query.name, location.query.name)
    ) {
      this.loadData();
      window.scrollTo(0, 0);
    }

    if (!isEqual(influencers, prevProps.influencers)) {
      this.setExpandedState(getMostActiveState(expandedIdsMap) || getTileSize('lists'));
    }
  }

  componentWillUnmount() {
    const { dispatchResetLists } = this.props;

    dispatchResetLists();
  }

  setExpandedState(value): void {
    const { influencers } = this.props;

    this.setState((prevState) => ({
      ...prevState,
      expandedIdsMap: influencers.reduce((res, creator) => {
        res[creator.id] = value;

        return res;
      }, {})
    }));
  }

  isManageableByOrganization(): boolean {
    const { location, organizationsListIds } = this.props;
    const orgId = Number(location?.query?.orgId);

    if (isNumber(orgId)) {
      return getManageListStatus(orgId, organizationsListIds);
    }

    return false;
  }

  getListManageStatus(): boolean {
    const { list } = this.props;
    return this.isManageableByOrganization() && list.type === 'custom';
  }

  handlePageChange = (page: number): void => {
    const { location } = this.props;

    updateQueryParams(location, { page });
  };

  handleGlobalResize = (value: string): void => {
    this.setExpandedState(value);
    setTileSize('lists', value);
  };

  handleListDeleted = (): void => {
    const { list } = this.props;
    const orgId = list?.organization?.id;

    if (orgId) {
      navigate.to(`/collections/creators?orgId=${orgId}`);
    }
  };

  handleDeleteInfluencer = (influencer: InfluencerTile): void => {
    const {
      location,
      list,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal,
      dispatchDeleteInfluencerFromList
    } = this.props;

    const orgId = Number(location?.query?.orgId);

    dispatchShowConfirmationModal({
      title: __t(`${msgKey}.modal_delete_influencer_title`, { listName: list.label }),
      message: '',
      confirmButtonLabel: __t(`${msgKey}.modal_delete_button_remove`),
      innerContent: this.renderConfirmationModal(influencer),
      onConfirm: () => {
        dispatchDeleteInfluencerFromList(orgId, list.id, influencer.id)
          .then(() => this.loadData());

        dispatchHideConfirmationModal();
      },
      onCancel: () => {
        dispatchHideConfirmationModal();
      }
    });
  };

  handleChannelChange = (channel: 'tiktok' | 'instagram' | 'all') => {
    this.setState({ channel }, this.loadListInfo);
  };

  handleBackButtonClick = (): void => {
    const prevListPath = getItem(LIST_STATE_STORAGE_NAME);

    navigate.to(prevListPath || '/collections/creators');
  };

  handleReplacePage(urlParams?: UrlParams): void {
    const { params, location } = this.props;
    const orgId = Number(location?.query?.orgId);
    const listId = Number(params.listId);
    const additionalParams = Querystring.objectToString(urlParams || {});

    navigate.replace(`/collections/creators/${listId}?page=1${additionalParams ? `&${additionalParams}` : ''}&orgId=${orgId}`);
  }

  handleUpdateSearchValue = (event: SyntheticEvent<TextEntry>): void => {
    const { location } = this.props;
    const target = event.currentTarget;
    const value = target.value || '';
    const orgId = Number(location?.query?.orgId);

    this.setState({
      searchByName: value
    }, () => {
      if (this.debounceMethod) {
        this.debounceMethod({ name: value, orgId });
      }
    });
  };

  handleCreatorTileSizeChange = (value: string, creatorId: number) => {
    this.setState((prevState) => ({
      ...prevState,
      expandedIdsMap: {
        ...prevState.expandedIdsMap,
        [creatorId]: value
      }
    }));
  };

  loadListInfo() {
    const { params, location, dispatchFetchList } = this.props;
    const { channel } = this.state;

    const orgId = Number(location?.query?.orgId);
    const listId = Number(params.listId);

    if (isNumber(orgId) && isNumber(listId)) {
      dispatchFetchList(orgId, listId, channel);
    }
  }

  loadData(): void {
    const { params, location, dispatchFetchList, dispatchFetchListInfluencers } = this.props;
    const { channel } = this.state;
    const orgId = Number(location?.query?.orgId);
    const listId = Number(params.listId);
    const name = getQueryValueFromUrl(location, 'name');
    const page = getQueryValueFromUrl(location, 'page');

    if (isNumber(orgId) && isNumber(listId)) {
      dispatchFetchList(orgId, listId, channel);
      dispatchFetchListInfluencers(orgId, listId, name, Number(page), { loadCreatorApprovalStatus: true });
    }
  }

  isCreatorsView(): boolean {
    return this.state.view === CREATORS;
  }

  isStatsView() {
    return this.state.view === STATS;
  }

  showStats = (trackingType: string): void => {
    const { list } = this.props;

    this.setState({
      view: STATS,
      channel: 'instagram'
    }, () => {
      trackEventListsStats('icon', list.id, list.type, trackingType, list.label);
      this.handleChannelChange('instagram');
    });
  };

  showCreators = (): void => {
    this.setState({
      view: CREATORS,
      channel: 'all'
    }, () => this.handleChannelChange('all'));
  };

  renderConfirmationModal(influencer: InfluencerTile) {
    const { creatorProposalStatus } = this.props;
    const message = hasProposalPage(creatorProposalStatus[String(influencer.id)]) ?
      `${__t(`${msgKey}.modal_delete_influencer_message`)}<br/>${__t(`${msgKey}.modal_delete_approved_proposal_influencer_message`)}` :
      __t(`${msgKey}.modal_delete_influencer_message`);

    return (
      <React.Fragment>
        <ModalFrameEntity influencerProfile={influencer} />
        <ModalFrameMessage
          message={message}
        />
      </React.Fragment>
    );
  }

  renderSort(): React.ReactNode {
    const { params, location, sort } = this.props;
    const { searchByName } = this.state;

    return (
      <div className="lists-view__sort">
        <ListsSort
          sort={sort}
          params={params}
          location={location}
          searchByName={searchByName}
        />
      </div>
    );
  }

  renderSearchByName(): React.ReactNode {
    const { searchByName } = this.state;

    return (
      <div className="lists-view__search">
        <Input
          name="searchByName"
          value={searchByName}
          autoComplete="off"
          placeholder={__t(`${msgKey}.search_by_name`)}
          onChange={this.handleUpdateSearchValue}
        />
      </div>
    );
  }

  renderTileSize(): React.ReactNode {
    const { expandedIdsMap } = this.state;

    return (
      <div className="lists-view__sizing">
        <CreatorTileSizing
          toggleIdsMap={expandedIdsMap}
          onGlobalResize={this.handleGlobalResize}
        />
      </div>
    );
  }

  renderCreatorProposal(): React.ReactNode {
    const { currentOrganization, location } = this.props;
    const orgId = Number(location?.query?.orgId);

    if (isNil(currentOrganization?.agencyOrganizationId)) {
      return null;
    }

    return (
      <span className="lists-view__open-proposal">
        <Tooltip
          message={__t(`${msgKey}.button_view_proposal`)}
          width="short"
        >
          <SVGDynamicIcon
            name="people-on-cards-custom-bg"
            width={24}
            height={24}
            customColor="grey"
            link={`${window.location.pathname}/proposal?orgId=${orgId}`}
          />
        </Tooltip>
      </span>
    );
  }

  renderStatsToggle(): React.ReactNode {
    return (
      <span className="lists-view__toggle-stats">
        <Tooltip
          message={__t(`${msgKey}.button_view_statistics`)}
          width="short"
        >
          <SVGDynamicIcon
            name="equalizer-custom-bg"
            width={22}
            height={24}
            customColor="grey"
            onClick={() => this.showStats('icon')}
          />
        </Tooltip>
      </span>
    );
  }

  renderHeader(): React.ReactNode {
    const { list } = this.props;
    const showCreatorProposal = list.type !== SMART_LIST_TYPE; // smart lists are not supported by creator proposal

    return (
      <div className="lists-view__header">
        <div className="lists-view__button">
          <ActionButton onClick={this.handleBackButtonClick}>
            &lt; {__t(`${msgKey}.button_return_to_lists`)}
          </ActionButton>
        </div>
        {
          this.isCreatorsView() && !isNil(list.numCreators) && list.numCreators !== 0 &&
            (
              <div className="lists-view__controls">
                {this.renderSort()}
                {this.renderSearchByName()}
                {this.renderTileSize()}
                {
                  showCreatorProposal &&
                    this.renderCreatorProposal()
                }
                {this.renderStatsToggle()}
              </div>
            )
        }
        {
          this.isStatsView() && (
            <span className="lists-view__close-stats">
              <Tooltip
                message={__t(`${msgKey}.label_view_creator_list`)}
                width="short"
              >
                <SVGDynamicIcon
                  name="cross-custom-fg"
                  width={14}
                  height={14}
                  customColor="grey"
                  onClick={this.showCreators}
                />
              </Tooltip>
            </span>
          )
        }
      </div>
    );
  }

  renderSidePanel(): React.ReactNode {
    const { list, location, isLoading } = this.props;
    const { channel, view } = this.state;

    return (
      <div className="lists-view__panel">
        <ListsViewPanel
          channel={channel}
          list={list}
          view={view}
          location={location}
          isLoading={isLoading}
          canManageList={this.getListManageStatus()}
          isManageableByOrganization={this.isManageableByOrganization()}
          onDelete={this.handleListDeleted}
          onShowStats={this.showStats}
          onShowCreators={this.showCreators}
        />
      </div>
    );
  }

  renderCreatorsList(): React.ReactNode {
    const { params, location, currentOrganization, influencers, screenState, isLoading } = this.props;
    const { expandedIdsMap } = this.state;
    const pagination = screenState.pagination;
    const manageListStatus = this.getListManageStatus();

    return (
      <div className="lists-view__results">
        <ListsResults
          orgId={currentOrganization.id}
          influencers={influencers}
          expandedIdsMap={expandedIdsMap}
          updateCreatorTileSize={this.handleCreatorTileSizeChange}
          canManageList={manageListStatus}
          contextMenuProps={{
            orgId: Number(location?.query?.orgId),
            listId: Number(params.listId)
          }}
          onDelete={this.handleDeleteInfluencer}
        />
        {
          !isLoading && (
            <Pagination
              location={location}
              meta={pagination}
              links={pagination.links}
              onClick={this.handlePageChange}
            />
          )
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { params, location } = this.props;
    const { channel } = this.state;

    return (
      <div className="lists-view">
        {this.renderHeader()}
        <div className="lists-view__body">
          {this.renderSidePanel()}
          {this.isCreatorsView() ? this.renderCreatorsList() : (
            <ListsStats
              orgId={Number(location?.query?.orgId)}
              listId={Number(params.listId)}
              channel={channel}
              onShowCreators={this.showCreators}
              onChannelChange={this.handleChannelChange}
            />
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ creatorProposalStatus, organizations, lists }): StateProps => ({
  currentOrganization: organizations.currentOrganization,
  organizationsListIds: organizations.list.map(organization => organization.id),
  influencers: lists.currentList.influencers,
  creatorProposalStatus: creatorProposalStatus.statuses,
  list: lists.currentList.list,
  sort: lists.screenState.sort,
  screenState: lists.screenState,
  isLoading: lists.currentList.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchList: (orgId: number, listId: number, channel: string): void => dispatch(fetchList(orgId, listId, channel === 'all' ? null : channel)),

  dispatchFetchListInfluencers: (
    orgId: number,
    listId: number,
    name: string,
    pageOverride: number | undefined | null,
    options: ListInfluencerOptions
  ): void => dispatch(fetchListInfluencers(orgId, listId, name, pageOverride, options)),

  dispatchResetLists: (): void => dispatch(resetLists()),

  dispatchDeleteInfluencerFromList: (orgId: number, listId: number, influencerId: number) => (
    dispatch(removeSingleInfluencerFromList(orgId, listId, influencerId))
  ),

  dispatchShowConfirmationModal: (props: ListConfirmModalProps) => (
    dispatch(showConfirmModal(props))
  ),

  dispatchHideConfirmationModal: () => (
    dispatch(hideConfirmModal())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(ListsView);
